import { CommandObject } from '../types';

/**
 * General Codes
 **/

export const NUL = '\x00';
export const DLE = '\x10';
export const ESC = '\x1b';
export const FS = '\x1c';
export const FF = '\x0c';
export const GS = '\x1d';
export const LF = '\x0a';
export const CR = '\x0d';
export const HT = '\x09';
export const SP = '\x20';

/**
 * Print commands and/or Basic control commands
 **/

export const initializePrinter = `${ESC}\x40`; // ESC @
export const printAndFeed1Line = `${FF}`; // FF
export const printAndFeedXLine = `${ESC}\x64`; // ESC d
export const printAndLineFeed = `${LF}`; // LF
export const printAndCarriageReturn = `${CR}`; // CR
export const printAndFeedMinimum = `${ESC}\x4a`; // ESC J
export const horizontalTabAndPrint = `${HT}`; // HT

// prettier-ignore
export const basicCommand: CommandObject = {
  [initializePrinter]: {
    description: 'Initialize printer',
  },
  [printAndFeed1Line]: {
    description: 'Print and feed paper 1 lines',
    categories: ['print', 'printAndNewLine'],
  },
  [printAndLineFeed]: {
    description: 'Print and line feed',
    categories: ['print', 'printAndNewLine'],
  },
  [printAndCarriageReturn]: {
    description: 'Print and carriage return',
    categories: ['print', 'printAndNewLine'],
  },
  [printAndFeedMinimum]: {
    description: 'Print and feed paper using minimum units',
    fixedArgumentLength: 1,
    categories: ['print', 'printAndNewLine'],
  },
  [printAndFeedXLine]: {
    description: 'Print and feed paper n lines',
    fixedArgumentLength: 1,
    categories: ['print', 'printAndNewLine'],
  },
  [horizontalTabAndPrint]: {
    description: 'Horizontal tab',
    categories: ['print'],
  },
};

/**
 * Character related commands
 **/

export const enableAsiaCharset = `${FS}\x26`; // FS &
export const selectCharSize = `${GS}\x21`; // GS !
export const turnWhiteBlackReversePrinting = `${GS}\x42`; // GS B
export const turnQuadrupleSizeMode = `${FS}\x57`; // FS W
export const selectCharPrintingMode = `${ESC}\x21`; // ESC !
export const turnUnderlineMode = `${ESC}\x2d`; // ESC -
export const turnEmphasizedMode = `${ESC}\x45`; // ESC E
export const turn90ClockwiseRotationMode = `${ESC}\x56`; // ESC V
export const turnItalicPrintMode = `${ESC}\x49`; // ESC I
export const turnUpsideDownPrintMode = `${ESC}\x7b`; // ESC {
export const cancelUserDefinedCharset = `${ESC}\x3f`; // ESC ?
export const selectCharCodeTable = `${ESC}\x74`; // ESC t
export const turnDoubleStrikeMode = `${ESC}\x47`; // ESC G
export const selectInternationalCharset = `${ESC}\x52`; // ESC R
export const selectCodeConversion = `${FS}\x28\x43`; // FS ( C
export const specialUnicodeEncoding = `${ESC}\x55\x4e\x49\x43\x4f\x44\x45`; // ESC U N I C O D E

// prettier-ignore
export const characterCommand: CommandObject = {
  [enableAsiaCharset]: {
    description: "Enable ASIA character set"
  },
  [selectCharSize]: {
    description: "Select character size", 
    fixedArgumentLength: 1
  },
  [turnWhiteBlackReversePrinting]: {
    description: "Turn white/black reverse printing mode on/off", 
    fixedArgumentLength: 1
  },
  [turnQuadrupleSizeMode]: {
    description: "Turn quadruple-size mode on/off for Kanji characters", 
    fixedArgumentLength: 1
  },
  [selectCharPrintingMode]: {
    description: "Select character printing mode", 
    fixedArgumentLength: 1
  },
  [turnUnderlineMode]: {
    description: "Turn underline mode on/off", 
    fixedArgumentLength: 1
  },
  [turnEmphasizedMode]: {
    description: "Turn emphasized mode on/off", 
    fixedArgumentLength: 1
  },
  [turn90ClockwiseRotationMode]: {
    description: "Turn 90° clockwise rotation mode on/off", 
    fixedArgumentLength: 1
  },
  [turnItalicPrintMode]: {
    description: "Turn italic print mode on/off", 
    fixedArgumentLength: 1
  },
  [turnUpsideDownPrintMode]: {
    description: "Turn upside-down printing mode on/off", 
    fixedArgumentLength: 1
  },
  [cancelUserDefinedCharset]: {
    description: "Cancel user-defined characters", 
    fixedArgumentLength: 1
  },
  [selectCharCodeTable]: {
    description: "Select character code table", 
    fixedArgumentLength: 1
  },
  
  // TODO: try to implement this
  [turnDoubleStrikeMode]: {
    description: "Turn double-strike character on/off", 
    fixedArgumentLength: 1
  }, 
  [selectInternationalCharset]: {
    description: "Select international character set", 
    fixedArgumentLength: 1
  }, 

  /**
   * Please refer more here as to what model this command is supported
   * https://reference.epson-biz.com/modules/ref_escpos/index.php?content_id=336
   * 
   * TODO: Test in the real printer, ask the iot team to try print this command
   *       and use the unicode code point and the unicode encodedURIComponent to see
   *       the difference
   */ 
  [selectCodeConversion]: {
    description: "Select code conversion method", 
    fixedArgumentLength: 4
  },

  /**
   * NOTE:
   * - This is a special command only to handle special case where an
   *   ESC/POS format was first given in a browser based textual data
   *   such that we have the control to convert a given unicode character
   *   to the ESC/POS binary format
   *   
   * - This command is and should not exist in the real ESC/POS format
   *   to avoid clashing commands
   * 
   * - If a Unicode character somehow found in the text data inside 
   *   convertTextToBinaryFormat() function then this command will
   *   be injected to the resulting binary
   * 
   * - This command will take all data after this command up to null bytes
   *   and treat that as unicode characters and put it in the print buffer
   * 
   * - This command will be optimized by the analyzer in mergeSpecialUnicodeCommand 
   *   function and should never goes to the renderer, otherwise there should 
   *   be a bug then
   */
  [specialUnicodeEncoding]: {
    description: "Special unicode encoding command"
  }
};

/**
 * Print layout/position commands
 **/

export const setAbsolutePrintPos = `${ESC}\x24`; // ESC $
export const setHorizontalTabPos = `${ESC}\x44`; // ESC D
export const selectPrintDirection = `${ESC}\x54`; // ESC T
export const setPrintingArea = `${ESC}\x57`; // ESC W
export const setRelativePrintPos = `${ESC}\x5c`; // ESC \
export const setDefaultLineSpacing = `${ESC}\x32`; // ESC 2
export const setLineSpacing = `${ESC}\x33`; // ESC 3
export const setBlackWhiteReversePrinting = `${ESC}\x34`; // ESC 4
export const setItalicPrintingOff = `${ESC}\x35`; // ESC 5
export const setCharacterRightSpaceAmount = `${ESC}${SP}`; // ESC SP
export const setPositionAlignment = `${ESC}\x61`; // ESC a
export const setLeftMargin = `${GS}\x4c`; // GS L
export const setAbsoluteVerticalPrintPos = `${GS}\x24`; // GS $
export const setPrintingAreaWidth = `${GS}\x57`; // GS W
export const setRelativeVerticalPrintPos = `${GS}\x5c`; // GS \

// prettier-ignore
export const layoutCommand: CommandObject = {
  [setAbsolutePrintPos]: {
    description: "Set absolute print position", 
    fixedArgumentLength: 2
  },
  [setHorizontalTabPos]: {
    description: "Set horizontal tab position"
  },
  [selectPrintDirection]: {
    description: "Select print direction in page mode", 
    fixedArgumentLength: 1
  },
  [setPrintingArea]: {
    description: "Set printing area in page mode", 
    fixedArgumentLength: 8
  },
  [setRelativePrintPos]: {
    description: "Set relative print position", 
    fixedArgumentLength: 2
  },
  [setDefaultLineSpacing]: {
    description: "Set default line spacing (1/6 inch)"
  },
  [setLineSpacing]: {
    description: "Set line spacing", 
    fixedArgumentLength: 1
  },
  [setBlackWhiteReversePrinting]: {
    description: "Set black-white reverse printing"
  },
  [setItalicPrintingOff]: {
    description: "Set italic printing off"
  },
  [setCharacterRightSpaceAmount]: {
    description: "Set character right space amount", 
    fixedArgumentLength: 1
  }, 
  [setPositionAlignment]: {
    description: "Set position alignment/justification", 
    fixedArgumentLength: 1
  }, 
  [setLeftMargin]: {
    description: "Set left margin", 
    fixedArgumentLength: 2
  },
  [setAbsoluteVerticalPrintPos]: {
    description: "Set absolute vertical print position in page mode", 
    fixedArgumentLength: 2
  },
  [setPrintingAreaWidth]: {
    description: "Set printing area width", 
    fixedArgumentLength: 2
  },
  [setRelativeVerticalPrintPos]: {
    description: "Set relative vertical print position in page mode", 
    fixedArgumentLength: 2
  },
};

/**
 * Graphics/Image related commands
 *
 * Note: Raster bitmap image format that is being used here
 *       turns out to be exactly like a PBM file format but
 *       without the header data
 *
 *       refer to https://oceancolor.gsfc.nasa.gov/staff/norman/seawifs_image_cookbook/faux_shuttle/pbm.html
 *       for the PBM file format specification
 **/

export const selectBitImageMode = `${ESC}\x2a`; // ESC *
export const defineDownloadedBitImage = `${GS}\x2a`; // GS *
export const printDownloadedBitImage = `${GS}\x2f`; // GS /
export const defineNVBitImage = `${FS}\x71`; // FS q
export const printNVBitImage = `${FS}\x70`; // FS p
export const printRasterBitImage = `${GS}\x76\x30`; // GS v 0
export const printAdvanceRasterGraphic = `${ESC}\x2e`; // ESC .
export const specifyGraphicsData = `${GS}\x28\x4c`; // GS ( L
export const specifyGraphicsDataAlternative = `${GS}\x38\x4c`; // GS 8 L

// prettier-ignore
export const graphicCommand: CommandObject = {
  [selectBitImageMode]: {
    description: "Select bit image mode", 
    fixedArgumentLength: 3
  },
  [defineDownloadedBitImage]: {
    description: "Define downloaded bit image", 
    fixedArgumentLength: 2
  },
  [printDownloadedBitImage]: {
    description: "Print downloaded bit image", 
    fixedArgumentLength: 1
  },
  [defineNVBitImage]: {
    description: "Define NV bit image", 
    fixedArgumentLength: 1
  },
  [printNVBitImage]: {
    description: "Print NV bit image", 
    fixedArgumentLength: 2
  },
  [printRasterBitImage]: {
    description: "Print raster bit image", 
    fixedArgumentLength: 5
  },

  /**
   * TODO: need a confirmation for this one via real world example, 
   *       as we need to match with the printer output
   * 
   * Note: refer to this one https://twitter.com/angealbertini/status/535565222652948480/photo/1
   *       for the TGA specification
   */
  [printAdvanceRasterGraphic]: {
    description: "Print Advanced Raster Graphics (presumably TGA)", 
    fixedArgumentLength: 4
  },

  /**
   * These two commands are basically the same
   */
  [specifyGraphicsData]: {
    description: "Specify graphics data", 
    fixedArgumentLength: 4
  }, 
  [specifyGraphicsDataAlternative]: {
    description: "Specify graphics data", 
    fixedArgumentLength: 4
  }, 
};

/**
 * QRcode/Barcode related commands
 **/

export const setBarcodeHeight = `${GS}\x68`; // GS h
export const setBarcodeWidth = `${GS}\x77`; // GS w
export const selectHRICharacterPrintPos = `${GS}\x48`; // GS H
export const selectHRICharacterFont = `${GS}\x66`; // GS f
export const printBarcode = `${GS}\x6b`; // GS k
export const print2dCodes = `${GS}\x28\x6b`; // GS ( k

// prettier-ignore
export const barcodeCommand: CommandObject = {
  [setBarcodeHeight]: {
    description: "Set barcode height", 
    fixedArgumentLength:  1
  },
  [setBarcodeWidth]: {
    description: "Set barcode width",  
    fixedArgumentLength: 1
  },
  [selectHRICharacterPrintPos]: {
    description: "Select HRI character print position", 
    fixedArgumentLength:  1
  },
  [selectHRICharacterFont]: {
    description: "Select HRI character font", 
    fixedArgumentLength:  1
  },
  [printBarcode]: {
    description: "Print barcode",  
    fixedArgumentLength: 1
  },

  /**
   * Please refer to https://reference.epson-biz.com/modules/ref_escpos/index.php?content_id=130
   * for more information since it holds the most complete and detailed explanation of this command
   */
  [print2dCodes]: {
    description: "Print all kinds of 2d codes (PDF417, QRCode, etc)", 
    fixedArgumentLength: 4
  }, 
};

/**
 * Miscellaneous commands
 *
 * Note: we will only parse these commands without
 *       doing anything with the data since it is
 *       not related with rendering at all
 **/

export const selectCutModeAndCutPaper = `${GS}\x56`; // GS V
export const generatePulse = `${ESC}\x70`; // ESC p
export const performFullKnifeCut = `${ESC}\x69`; // ESC i
export const eraseStorageFlashMemory = `${GS}\x70`; // GS @
export const dotCheckPatternTest = `${GS}\x64`; // GS d

// prettier-ignore
export const miscCommand: CommandObject = {
  [selectCutModeAndCutPaper]: {
    description: "Select cut mode and cut paper", 
    fixedArgumentLength: 1
  },
  [generatePulse]: {
    description: "Generate pulse", 
    fixedArgumentLength: 3
  },
  [performFullKnifeCut]: {
    description: "Perform full knife cut", 
  },
  [eraseStorageFlashMemory]: {
    description: "Erase user-defined storage area in flash memory", 
    fixedArgumentLength: 1
  },

  // TODO: this is different for each printer, so need to check for all different specs
  [dotCheckPatternTest]: {
    description: "Dot check pattern test", 
    fixedArgumentLength: 1
  },
};

const getCommandsByCategory = (category: string) => {
  const combinedCommands = {
    ...basicCommand,
    ...characterCommand,
    ...layoutCommand,
    ...graphicCommand,
    ...barcodeCommand,
    ...miscCommand,
  };

  return Object.entries(combinedCommands)
    .filter(([, content]) => content.categories?.includes(category))
    .map(([command]) => command);
};

export const printCommands = getCommandsByCategory('print');
export const printAndNewLineCommands = getCommandsByCategory('printAndNewLine');
